import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Form, Input } from 'antd';
import dayjs from 'dayjs';
import { ArrowLeftIcon, CalendarIcon } from 'icons';
import { useTranslations } from 'next-intl';

import { useAppDispatch, useAppSelector } from 'store';

import { setUser } from 'modules/global';

import { useCreateUser } from '../hooks';
import { prevStep } from '../store';

import styles from './index.module.scss';

export interface IProfileV2 {
  full_name: string;
  last_name: string;
  type: string | null;
  birthday: string;
  gender: string;
  address_id: number | null;
  subject_ids: number[];
}

const RegisterStep = () => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const [selected, setSelected] = useState<'erkak' | 'ayol'>('erkak');
  const type = useAppSelector(state => state.authModal.data.role);
  const subjects = useAppSelector(state => state.authModal.data.subjectIds);
  const regionId = useAppSelector(state => state.authModal.data.region);
  const user = useAppSelector(state => state.user);
  const [form] = Form.useForm();
  const { mutate, isLoading } = useCreateUser();

  const genders = [
    { label: t('auth.gender_male_label'), value: 'erkak' },
    { label: t('auth.gender_female_label'), value: 'ayol' }
  ];

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        full_name: user.name,
        last_name: user.last_name,
        birthday: dayjs(user.birthday),
        gender: user.gender
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRegister = (values: IProfileV2) => {
    mutate({
      ...values,
      type,
      subject_ids: subjects,
      gender: selected,
      address_id: regionId,
      birthday: dayjs(values.birthday).format('YYYY-MM-DD')
    });
  };

  return (
    <>
      <Form form={form} onFinish={onRegister} className="flex h-[94vh] w-full flex-col justify-between p-6 sm:h-full">
        <div className="p-6">
          <h3 className="mb-2 text-3xl font-semibold">{t('profilingizni_toldiring')}</h3>
          <p className=" font-[500]">{t('profilingizni_toldiring_desc')}</p>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="h-[60vh] w-full max-w-sm sm:h-full">
            <Form.Item name="full_name" rules={[{ required: true, message: t('auth.first_name_validation_message') }]}>
              <Input
                autoFocus
                className={`${styles.customInput} !h-13`}
                size="large"
                placeholder={t('auth.first_name_placeholder')}
              />
            </Form.Item>
            <Form.Item name="last_name" rules={[{ required: true, message: t('auth.last_name_validation_message') }]}>
              <Input
                className={`${styles.customInput} !h-13`}
                size="large"
                placeholder={t('auth.last_name_placeholder')}
              />
            </Form.Item>
            <Form.Item name="birthday" rules={[{ required: true, message: t('auth.birth_date_validation_message') }]}>
              <DatePicker
                className={`${styles.customDatePicker} !h-13`}
                size="large"
                suffixIcon={<CalendarIcon />}
                placeholder={t('auth.birth_date_placeholder')}
                format="DD.MM.YYYY"
                disabledDate={current => {
                  const today = dayjs();
                  const minDate = today.subtract(70, 'year');
                  const maxDate = today.subtract(7, 'year');

                  return current && (current > maxDate || current < minDate);
                }}
                onChange={date => {
                  if (date?.isValid()) {
                    dispatch(setUser({ birthday: date.format('YYYY.MM.DD') }));
                  }
                }}
              />
            </Form.Item>

            <div className="flex justify-evenly">
              {genders.map(gender => {
                const isSelected = selected === gender.value;

                return (
                  <div
                    key={gender.value}
                    onClick={() => setSelected(gender.value as 'erkak' | 'ayol')}
                    className="flex cursor-pointer items-center space-x-2 transition duration-200"
                  >
                    <div
                      className={`flex h-5 w-5 items-center justify-center rounded-full border-2
                      ${isSelected ? 'border-green-500' : 'border-gray-300'}`}
                    >
                      {isSelected && <div className="h-3 w-3 rounded-full bg-green-500" />}
                    </div>
                    <span className="text-lg">{gender.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between p-6">
          <div className="text-gray-400">7/8</div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => dispatch(prevStep())}
              className="!py-5.5 !rounded-lg !border-none !border-transparent !bg-gray-100 !px-4 hover:!text-black"
            >
              <div className="mt-0.5">
                <ArrowLeftIcon size="18" />
              </div>
              {t('ortga')}
            </Button>
            <Button
              loading={isLoading}
              htmlType="submit"
              className="!rounded-lg !border-transparent !bg-green-500 !px-5 !py-5 !text-white disabled:!bg-gray-100 disabled:!text-gray-400"
            >
              {t('auth.registration_title')}
            </Button>
          </div>
        </div>
      </Form>
    </>
  );
};

export default RegisterStep;
